import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext';
import Header from './components/Header';
import Start from './components/Start';
import Checker from './components/Checker/Cheker';
import ResultPage from './components/ResultPage';
import ThankYouPage from './components/ThankYouPage';
import CustomerServicePage from './components/CustomerService';
import { useCmsText } from './hooks/useCmsText';
import { Branding } from './context/AppContext';
import { resolveCountry } from './utils/geoLocation';
import { saveAccessToken, saveSelectedPartner, saveArticles, isUserLogged, markUserAsLogged } from './utils/storage';
import {
  useGetPartnerDataMutation,
  useGetCmsDataMutation,
  useGetArticlesByPartnerMutation,
  useGenerateKoncivTokenMutation,
  useLogActiveUserMutation,
} from './store/api';

const SKJOLD_EMAIL = process.env.REACT_APP_SKJOLD_EMAIL || '';
const SKJOLD_PASSWORD = process.env.REACT_APP_SKJOLD_PASSWORD || '';

const SUPPORTED_COUNTRY_LANGUAGE_MAP: Record<string, 'NORSK' | 'DANSK' | 'SVENSK' | 'ENG'> = {
  NORSK: 'NORSK',
  DANSK: 'DANSK',
  SVENSK: 'SVENSK',
};

const DEFAULT_LOADING_BY_LANG: Record<'NORSK' | 'DANSK' | 'SVENSK' | 'ENG', string> = {
  NORSK: 'Laster inn',
  DANSK: 'Indlæser',
  SVENSK: 'Laddar',
  ENG: 'Loading',
};

const Loader = ({ text }: { text: string | JSX.Element }) => (
  <div className="w-full h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="loader border-4 border-t-4 border-gray-200 h-12 w-12 mb-4 rounded-full animate-spin mx-auto"></div>
      <p className="text-gray-600">{text}...</p>
    </div>
  </div>
);

const AppRoutes = () => {
  const { state, dispatch } = useAppContext();
  const { renderTextForLanguage } = useCmsText();
  const [loading, setLoading] = useState(true);
  const [loadingLang, setLoadingLang] = useState<'NORSK' | 'DANSK' | 'SVENSK' | 'ENG'>('ENG');

  const [getPartnerData] = useGetPartnerDataMutation();
  const [getCmsData] = useGetCmsDataMutation();
  const [getArticlesByPartner] = useGetArticlesByPartnerMutation();
  const [generateKoncivToken] = useGenerateKoncivTokenMutation();
  const [logActiveUser] = useLogActiveUserMutation();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { code } = await resolveCountry(dispatch);
      const lang =
        (code && SUPPORTED_COUNTRY_LANGUAGE_MAP[code.toUpperCase()]) || 'ENG';
      if (!cancelled) setLoadingLang(lang);
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchPartnerData = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const partner = urlParams.get('partner');

      if (partner) {
        dispatch({ type: 'SET_PARTNER', payload: partner });
        saveSelectedPartner(partner);

        try {
          // Generate Konciv access token for backend API calls
          let accessToken = '';
          try {
            const tokenResponse = await generateKoncivToken({
              email: SKJOLD_EMAIL,
              password: SKJOLD_PASSWORD,
            }).unwrap();
            accessToken = tokenResponse.access_token || tokenResponse.accessToken || '';
            if (accessToken) {
              dispatch({ type: 'SET_ACCESS_TOKEN', payload: accessToken });
              saveAccessToken(accessToken);
            }
          } catch (tokenError) {
            console.error('Error generating Konciv token:', tokenError);
          }

          // Fetch partner branding + contact info and CMS data in parallel
          const fetchPromises: Promise<any>[] = [
            getPartnerData({ partner }).unwrap(),
          ];

          if (accessToken) {
            fetchPromises.push(
              getCmsData({ accessToken, partnerName: partner }).unwrap()
            );
            fetchPromises.push(
              getArticlesByPartner({ accessToken, partnerName: partner }).unwrap()
            );
          }

          const [partnerResponse, cmsData, articles] = await Promise.all(
            fetchPromises.map((p) => p.catch((e: any) => { console.error(e); return null; }))
          );

          // Process CMS data (from Konciv backend if available, else fall back to partner response)
          if (cmsData && cmsData.cms) {
            dispatch({ type: 'SET_CMS_DATA', payload: cmsData });

            if (cmsData.languages && cmsData.languages.length > 0) {
              dispatch({ type: 'SET_LANGUAGES', payload: cmsData.languages });
              const defaultLanguage =
                cmsData.languages.find((lang: any) => lang.code === 'NORSK') ||
                cmsData.languages[0];
              if (defaultLanguage) {
                dispatch({ type: 'SET_LANGUAGE', payload: defaultLanguage.code });
              }
            }
          } else if (partnerResponse && partnerResponse.cms) {
            // Fallback: use partner response CMS data
            dispatch({ type: 'SET_CMS_DATA', payload: partnerResponse });

            const cmsLanguages = partnerResponse.cms.map((item: any) => ({
              name: item?.name,
              code:
                item?.propertyValues.find(
                  (p: any) => p.definition.name === 'Language'
                )?.value,
              isDefault: item?.propertyValues.some(
                (p: any) =>
                  p.definition.name === 'Default' && p.value === 'Yes'
              ),
            }));

            dispatch({ type: 'SET_LANGUAGES', payload: cmsLanguages });

            const defaultLanguage = cmsLanguages.find((lang: any) => lang.isDefault);
            if (defaultLanguage) {
              dispatch({ type: 'SET_LANGUAGE', payload: defaultLanguage.code });
            } else if (cmsLanguages.length > 0) {
              dispatch({ type: 'SET_LANGUAGE', payload: cmsLanguages[0].code });
            }
          }

          // Process articles
          if (articles && Array.isArray(articles)) {
            dispatch({ type: 'SET_ARTICLES', payload: articles });
            saveArticles(articles);
          }

          // Process branding and contact info from partner response
          if (partnerResponse) {
            const contactString = partnerResponse.value;
            if (typeof contactString === 'string') {
              const phoneMatch = contactString.match(/phone:([^;]+)/);
              const emailMatch = contactString.match(/email:([^;]+)/);
              if (phoneMatch) {
                dispatch({ type: 'SET_PARTNER_PHONE', payload: phoneMatch[1] });
              }
              if (emailMatch) {
                dispatch({ type: 'SET_PARTNER_EMAIL', payload: emailMatch[1] });
              }
            }

            if (partnerResponse?.branding && partnerResponse?.branding?.length > 0) {
              const brandingItem = partnerResponse.branding[0];
              const brandingProps = brandingItem.propertyValues.reduce(
                (acc: Record<string, string>, prop: any) => {
                  if (
                    prop.definition.name === 'Logo' &&
                    prop.files &&
                    prop.files.length > 0
                  ) {
                    acc['Logo'] = prop.files[0].filePath;
                  } else {
                    acc[prop.definition.name] = prop.value;
                  }
                  return acc;
                },
                {}
              );

              const branding: Branding = {
                mainBackground: brandingProps['Main background'] || '#ffffff',
                boxBackground: brandingProps['Box background'] || '#f0f0f0',
                footerBoxBackground: brandingProps['Footer box background'] || '#e0e0e0',
                fontColor: brandingProps['Font color'] || '#000000',
                mainButtonBackground: brandingProps['Main Button background'] || '#007bff',
                mainButtonFont: brandingProps['Main Button font'] || '#ffffff',
                secondaryButtonBackground: brandingProps['Secondary button background'] || '#e9ecef',
                secondaryButtonBorder: brandingProps['Secondary button border'] || '#ced4da',
                secondaryButtonFont: brandingProps['Secondary button font'] || '#212529',
                thirdButtonBackground: brandingProps['Third button background'] || '#6c757d',
                thirdButtonBorder: brandingProps['Third button border'] || '#6c757d',
                thirdButtonFont: brandingProps['Third button font'] || '#ffffff',
                logo: brandingProps['Logo'] || '',
              };

              dispatch({ type: 'SET_BRANDING', payload: branding });
            }
          }

          // Log active user (fire-and-forget, once per session)
          if (accessToken && !isUserLogged()) {
            logActiveUser({
              partnerName: partner,
              invitationCode: '',
              deviceInfo: { platform: 'web' },
            })
              .then(() => markUserAsLogged())
              .catch((err) => console.log('Failed to log active user:', err));
          }
        } catch (error) {
          console.error('Error fetching partner data:', error);
        }
      }

      setLoading(false);
    };

    fetchPartnerData();
  }, [dispatch, getPartnerData, getCmsData, getArticlesByPartner, generateKoncivToken, logActiveUser]);

  const loadingTextElement = useMemo(() => {
    const cmsText = renderTextForLanguage(
      'loading_text',
      loadingLang,
      DEFAULT_LOADING_BY_LANG[loadingLang]
    );
    return cmsText;
  }, [loadingLang, renderTextForLanguage]);

  if (loading) {
    return <Loader text={loadingTextElement} />;
  }

  return (
    <div
      className="w-full min-h-screen flex flex-col"
      style={{
        backgroundColor: state.branding?.mainBackground,
        color: state.branding?.fontColor,
      }}
    >
      <Header />
      <main className="w-full flex flex-col items-center flex-1 overflow-y-auto">
        <Routes>
          <Route path="/" element={<Start />} />
          <Route path="/text-checker" element={<Checker type="text" />} />
          <Route path="/image-checker" element={<Checker type="image" />} />
          <Route path="/result" element={<ResultPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/customer-service" element={<CustomerServicePage />} />
        </Routes>
      </main>
    </div>
  );
};

export default function App() {
  return (
    <AppProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AppProvider>
  );
}
